import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PlaylistApi } from './PlaylistApi';
import { PLAYLIST_QUERY_KEYS } from '@/lib/constants/playlist';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { createBatchMutationHook } from '@/shared/hooks/useBatchMutation';
import type {
  CreateCustomPlaylistRequest, CreateTagPlaylistRequest,
  UpdateCustomPlaylistRequest, UpdateTagPlaylistRequest,
} from '@/lib/types/Playlist';

export function useCreateCustomPlaylist() {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req: CreateCustomPlaylistRequest) =>
      PlaylistApi.createCustomPlaylist(selectedCId!, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PLAYLIST_QUERY_KEYS.all(selectedCId!) });
    },
  });
}

export function useCreateTagPlaylist() {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req: CreateTagPlaylistRequest) =>
      PlaylistApi.createTagPlaylist(selectedCId!, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PLAYLIST_QUERY_KEYS.all(selectedCId!) });
    },
  });
}

export function useUpdateCustomPlaylist(listId: number) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req: UpdateCustomPlaylistRequest) =>
      PlaylistApi.updateCustomPlaylist(selectedCId!, listId, req),
    onSuccess: (updated) => {
      queryClient.setQueryData(
        PLAYLIST_QUERY_KEYS.customDetail(selectedCId!, listId),
        updated
      );
      queryClient.invalidateQueries({ queryKey: PLAYLIST_QUERY_KEYS.all(selectedCId!) });
    },
  });
}

export function useUpdateTagPlaylist(listId: number) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req: UpdateTagPlaylistRequest) =>
      PlaylistApi.updateTagPlaylist(selectedCId!, listId, req),
    onSuccess: (updated) => {
      queryClient.setQueryData(
        PLAYLIST_QUERY_KEYS.tagDetail(selectedCId!, listId),
        updated
      );
      queryClient.invalidateQueries({ queryKey: PLAYLIST_QUERY_KEYS.all(selectedCId!) });
    },
  });
}

/** Activate playlists (batch). */
export const useActivatePlaylists = createBatchMutationHook(
  PlaylistApi.activatePlaylists,
  (cId) => PLAYLIST_QUERY_KEYS.all(cId),
);

/** Deactivate playlists (batch). */
export const useDeactivatePlaylists = createBatchMutationHook(
  PlaylistApi.deactivatePlaylists,
  (cId) => PLAYLIST_QUERY_KEYS.all(cId),
);

/** Delete playlists (batch). */
export const useDeletePlaylists = createBatchMutationHook(
  PlaylistApi.deletePlaylists,
  (cId) => PLAYLIST_QUERY_KEYS.all(cId),
);
